import { useMemo } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import { articleData, mangaList } from '@/mocks/mangaData';

const articleJsonLd = (article: typeof articleData[string]) => [
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://readdy.ai' },
      { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://readdy.ai/category/all' },
      { '@type': 'ListItem', position: 3, name: article.mangaTitle, item: `https://readdy.ai/article/${article.id}` },
      { '@type': 'ListItem', position: 4, name: article.chapter },
    ],
  },
  {
    '@type': 'Article',
    headline: `${article.mangaTitle} ${article.chapter} - ${article.title}`,
    description: `免费在线阅读${article.mangaTitle} ${article.chapter}：${article.title}。刃牙漫画下拉式阅读，无需充值，全部章节免费畅读。`,
    datePublished: article.date,
    author: { '@type': 'Person', name: '板垣恵介' },
  },
];

function getArticleById(id: string | undefined) {
  if (!id || !articleData[id]) {
    return null;
  }
  return articleData[id];
}

export default function ArticlePage() {
  const params = useParams();
  const id = params.id as string | undefined;

  const article = useMemo(() => getArticleById(id), [id]);
  const mangaInfo = useMemo(
    () => article ? mangaList.find((m) => m.id === article.mangaId) : null,
    [article],
  );

  if (!article) {
    return (
      <>
        <SEOHead
          title="章节未找到 - 刃牙漫画"
          description="抱歉，未找到该漫画章节。请浏览刃牙漫画其他免费章节。"
        />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">章节未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，该漫画章节不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-3-line"></i>
              返回首页
            </Link>
          </div>
        </main>
      </>
    );
  }

  const paragraphs = article.content.split('\n').filter((p) => p.trim());

  return (
    <>
      <SEOHead
        title={`${article.mangaTitle} ${article.chapter} - ${article.title} | 刃牙漫画免费阅读`}
        description={`免费在线阅读${article.mangaTitle} ${article.chapter}：${article.title}。刃牙漫画下拉式即时阅读，无需充值，全部章节免费畅读。`}
        keywords={`${article.mangaTitle},刃牙漫画免费,免费看刃牙,${article.chapter},刃牙漫画在线观看`}
        canonicalUrl={`https://readdy.ai/article/${article.id}`}
        ogType="article"
        jsonLd={articleJsonLd(article)}
      />

      <main className="min-h-screen pt-20 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/category/all" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">漫画分类</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800">{article.mangaTitle}</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-primary-600 font-medium">{article.chapter}</span>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              {article.mangaTitle} {article.chapter}
            </h1>
            <h2 className="text-lg text-foreground-600 mb-4">{article.title}</h2>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                板垣恵介
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              {mangaInfo && (
                <span className="flex items-center gap-1">
                  <i className="ri-bookmark-line"></i>
                  {mangaInfo.category}
                </span>
              )}
            </div>
          </div>

          <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-10 mb-10">
            <div className="prose max-w-none">
              {paragraphs.map((para, index) => (
                <p
                  key={index}
                  className="text-base leading-loose text-foreground-800 mb-4"
                  style={{ textIndent: '2em' }}
                >
                  {para}
                </p>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            {article.prevChapter ? (
              <Link
                to={`/article/${article.prevChapter.id}`}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                上一章：{article.prevChapter.title}
              </Link>
            ) : (
              <div />
            )}

            {article.nextChapter ? (
              <Link
                to={`/article/${article.nextChapter.id}`}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                下一章：{article.nextChapter.title}
                <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <span className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-secondary-100 text-secondary-700 text-sm whitespace-nowrap">
                <i className="ri-check-line"></i>
                已是最新章节
              </span>
            )}
          </div>

          <div className="mt-10 p-5 rounded-lg bg-background-100 border border-background-200/70">
            <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
              关于{article.mangaTitle}
            </h3>
            <p className="text-sm text-foreground-600 mb-4">
              {mangaInfo?.description || `${article.mangaTitle}是刃牙系列漫画的重要作品，由板垣恵介创作，讲述了热血格斗的精彩故事。`}
            </p>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              浏览更多刃牙漫画
            </Link>
          </div>
        </article>
      </main>
    </>
  );
}